export const SERIES_RECENT_LIMIT = 200;
export const SERIES_POPULAR_LIMIT = 18;

const SERIES_WEEKDAYS = [
  'pazartesi',
  'sali',
  'carsamba',
  'persembe',
  'cuma',
  'cumartesi',
  'pazar',
];

const normalizePlatformName = (value) => String(value || '')
  .toLocaleLowerCase('tr-TR')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/\u0131/g, 'i');

export const resolveKnownSeriesPlatformId = (value) => {
  const normalizedName = normalizePlatformName(value);
  const platformTokens = normalizedName.split(/[^a-z0-9]+/).filter(Boolean);
  const hasToken = (token) => platformTokens.includes(token);

  if (hasToken('netflix')) return 'netflix';
  if (hasToken('amazon') || hasToken('prime')) return 'amazon';
  if (hasToken('disney')) return 'disney';
  if (hasToken('hbo') || hasToken('max')) return 'hbo';
  if (hasToken('tod')) return 'tod';
  if (hasToken('exxen')) return 'exxen';
  if (hasToken('gain')) return 'gain';

  return null;
};

export const getSeriesPlatformPriority = (name) => {
  const normalizedName = normalizePlatformName(name);

  if (normalizedName.includes('netflix')) return 1;
  if (normalizedName.includes('amazon') || normalizedName.includes('prime')) return 2;
  if (normalizedName.includes('disney')) return 3;
  if (normalizedName.includes('hbo') || normalizedName.includes('max')) return 4;
  if (normalizedName.includes('apple')) return 5;
  if (normalizedName.includes('hulu')) return 6;
  if (normalizedName.includes('paramount')) return 7;
  if (normalizedName.includes('blutv') || normalizedName.includes('blu tv')) return 8;
  if (normalizedName.includes('exxen')) return 9;
  if (normalizedName.includes('gain')) return 10;
  if (normalizedName.includes('tod')) return 11;
  if (normalizedName.includes('tabii')) return 12;
  if (normalizedName.includes('mubi')) return 13;
  if (normalizedName.includes('tiviplus')) return 14;
  if (normalizedName.includes('tv dizileri')) return 15;

  const platformTokens = normalizedName.split(/[^a-z0-9]+/).filter(Boolean);
  const weekdayIndex = SERIES_WEEKDAYS.findIndex((dayName) => platformTokens.includes(dayName));
  if (weekdayIndex >= 0) return 900 + weekdayIndex;

  return 50;
};

export const sortSeriesPlatformsByImportance = (platforms = []) => platforms
  .map((platform, serverIndex) => ({ platform, serverIndex }))
  .sort((first, second) => (
    getSeriesPlatformPriority(first.platform.name) - getSeriesPlatformPriority(second.platform.name)
    || first.serverIndex - second.serverIndex
  ))
  .map(({ platform }) => platform);

const positiveNumericValue = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : 0;
};

export const getSeriesAddedValue = (series) => (
  positiveNumericValue(series?.added)
  || positiveNumericValue(series?.series_id)
  || positiveNumericValue(series?.id)
);

const getRecentlyAddedRank = (series) => {
  const rank = series?.recentlyAddedRank;
  if (rank == null || rank === '') return null;
  const numericRank = Number(rank);
  return Number.isFinite(numericRank) && numericRank >= 0 ? numericRank : null;
};

export const sortSeriesNewestFirst = (series = []) => series
  .map((item, serverIndex) => ({ item, serverIndex }))
  .sort((first, second) => {
    const firstRank = getRecentlyAddedRank(first.item);
    const secondRank = getRecentlyAddedRank(second.item);

    if (firstRank != null && secondRank != null && firstRank !== secondRank) {
      return firstRank - secondRank;
    }
    if (firstRank != null && secondRank == null) return -1;
    if (firstRank == null && secondRank != null) return 1;

    return getSeriesAddedValue(second.item) - getSeriesAddedValue(first.item)
      || first.serverIndex - second.serverIndex;
  })
  .map(({ item }) => item);

export const selectRecentlyAddedSeries = (series = [], limit = SERIES_RECENT_LIMIT) => (
  sortSeriesNewestFirst(series).slice(0, Math.max(0, limit))
);

const getSeriesRating = (series) => {
  const numericRating = Number.parseFloat(
    series?.rating ?? series?.imdbRating ?? series?.imdb_rating ?? 0
  );
  return Number.isFinite(numericRating) ? numericRating : 0;
};

const getSeriesIdentity = (series, fallbackIndex = 0) => String(
  series?.series_id || series?.id || `${series?.name || series?.title || 'series'}-${fallbackIndex}`
);

const hashText = (value) => {
  let hash = 2166136261;
  const text = String(value || '');

  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
};

export const selectDailyPopularSeries = (
  series = [],
  limit = SERIES_POPULAR_LIMIT,
  dateKey = new Date().toISOString().slice(0, 10)
) => {
  const seenIds = new Set();

  return series
    .map((item, serverIndex) => ({ item, serverIndex, id: getSeriesIdentity(item, serverIndex) }))
    .filter(({ item, id }) => {
      if (getSeriesRating(item) < 7 || seenIds.has(id)) return false;
      seenIds.add(id);
      return true;
    })
    .sort((first, second) => (
      hashText(`${dateKey}:${first.id}`) - hashText(`${dateKey}:${second.id}`)
      || first.serverIndex - second.serverIndex
    ))
    .slice(0, Math.max(0, limit))
    .map(({ item }) => item);
};

export const formatSeriesCount = (value) => {
  const numericValue = Number(value);
  return (Number.isFinite(numericValue) ? numericValue : 0).toLocaleString('tr-TR');
};
